import { FilterState, Selection } from 'components/DataTable/DataTable';
import { Form, FormInput } from 'components/Form/next';
import Loader from 'components/Loader';
import { TemplateFields } from 'components/MessageManagement/Template/TemplateFields';
import useToasts from 'components/ToastManager/useToasts';
import { useConfirmedCallback } from 'hooks/useConfirmedCallback';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useApi } from '~/hooks/useApi';
import { ClubMember } from '../../types';

interface SharedAddress {
    email: string;
    members: number;
}

interface AccountPlan {
    members_in_scope: number;
    accounts_to_create: number;
    members_to_link: number;
    skipped_no_email: number;
    skipped_has_account: number;
    skipped_shared_address: number;
    shared_addresses: SharedAddress[];
    max_members_per_address: number;
}

interface Props {
    clubSlug: string;
    members: Selection;
    filter?: FilterState;
    showAll?: boolean;
    member?: ClubMember;
    callback?: () => void;
    onFinish?: () => void;
    onUpdated?: (member: ClubMember) => void;
}

export const ClubMemberAccountForm = ({
    clubSlug,
    members,
    filter,
    showAll,
    member,
    callback,
    onFinish,
    onUpdated
}: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const { addToast } = useToasts();
    const [isLoading, setIsLoading] = useState(false);
    const [plan, setPlan] = useState<AccountPlan>();
    const [isLoadingPlan, setIsLoadingPlan] = useState(!member);

    useEffect(() => {
        if (member) return;

        void api({
            url: `club-member/${clubSlug}/accounts/preview`,
            method: 'POST',
            data: { members, filter, showAll },
            muted: true,
            onBefore: () => setIsLoadingPlan(false),
            onSuccess: (data) => setPlan(data)
        });
        // the preview describes the selection the modal was opened with, it never changes while open
    }, []);

    // Every new account gets its password mailed out the moment this runs, and that cannot be
    // taken back — so the bulk variant asks first and names what it is about to send.
    const submitMultiple = useConfirmedCallback(
        (data: FieldValues) => {
            setIsLoading(true);

            return api({
                url: `club-member/${clubSlug}/accounts`,
                method: 'POST',
                data: { ...data, members, filter, showAll },
                onBefore: () => setIsLoading(false),
                // fires when the progress toast reaches 100%, which outlives this modal
                toastCallback: onFinish,
                onSuccess: () => callback?.()
            });
        },
        {
            header: t('clubMember.accounts.confirmHeader'),
            message: () =>
                t('clubMember.accounts.confirmMessage', {
                    accounts: plan?.accounts_to_create ?? 0,
                    members: plan?.members_to_link ?? 0
                })
        }
    );

    const handleSubmit = (data: FieldValues) => {
        if (member) {
            setIsLoading(true);

            return api({
                url: `club-member/${member.id}/account`,
                method: 'POST',
                data,
                onBefore: () => setIsLoading(false),
                onSuccess: (response) => {
                    // the modal closes right away, so the outcome goes to a toast
                    addToast({
                        type: response?.status === 'skipped' ? 'error' : 'success',
                        title: t('clubMember.accounts.submitSingle'),
                        description: t(`clubMember.accounts.result.${response?.status ?? 'skipped'}`)
                    });
                    if (response?.data) onUpdated?.(response.data);
                    callback?.();
                }
            });
        }

        return submitMultiple(data);
    };

    // skippedSharedAddress sits in here so the five numbers actually add up to members_in_scope
    const rows: [string, number][] = plan
        ? [
              [t('clubMember.accounts.summary.inScope'), plan.members_in_scope],
              [t('clubMember.accounts.summary.toCreate'), plan.accounts_to_create],
              [t('clubMember.accounts.summary.toLink'), plan.members_to_link],
              [t('clubMember.accounts.summary.skippedNoEmail'), plan.skipped_no_email],
              [t('clubMember.accounts.summary.skippedSharedAddress'), plan.skipped_shared_address ?? 0],
              [t('clubMember.accounts.summary.skippedHasAccount'), plan.skipped_has_account]
          ]
        : [];

    const sharedAddresses = plan?.shared_addresses ?? [];

    return (
        <Loader isLoading={isLoadingPlan}>
            <p className="tw-mb-4 tw-text-sm">{t('clubMember.accounts.intro')}</p>

            {!member && !!plan && (
                <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-accents-3 tw-p-4">
                    {rows.map(([label, value]) => (
                        <div key={label} className="tw-flex tw-justify-between tw-py-1 tw-text-sm">
                            <span className="tw-opacity-70">{label}</span>
                            <span className="tw-font-semibold">{value}</span>
                        </div>
                    ))}
                </div>
            )}

            {!member && sharedAddresses.length > 0 && (
                <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-warning tw-bg-warning-light tw-p-4">
                    <p className="tw-mb-1 tw-text-sm tw-font-semibold">{t('clubMember.accounts.sharedTitle')}</p>
                    <p className="tw-mb-3 tw-text-xs tw-opacity-80">
                        {t('clubMember.accounts.sharedHint', { max: plan?.max_members_per_address })}
                    </p>
                    <ul className="tw-m-0 tw-max-h-40 tw-list-none tw-overflow-y-auto tw-p-0">
                        {sharedAddresses.map((address) => (
                            <li
                                key={address.email}
                                className="tw-flex tw-justify-between tw-gap-4 tw-py-0.5 tw-text-xs"
                            >
                                <span className="tw-truncate">{address.email}</span>
                                <span className="tw-whitespace-nowrap tw-font-semibold">
                                    {t('clubMember.accounts.sharedMembers', { value: address.members })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {member && !member.email && (
                <p className="tw-mb-4 tw-text-sm tw-text-error">{t('clubMember.accounts.noEmail')}</p>
            )}

            <Form onSubmit={handleSubmit}>
                <TemplateFields clubSlug={clubSlug} />
                <FormInput.Textarea
                    name="message"
                    label={t('clubMember.accounts.message')}
                    placeholder={t('clubMember.accounts.messagePlaceholder')}
                />
                <p className="tw-my-2 tw-text-xs tw-opacity-70">{t('clubMember.accounts.familyHint')}</p>
                <p className="tw-mb-4 tw-text-xs tw-opacity-70">{t('clubMember.accounts.passwordHint')}</p>
                <FormInput.Submit
                    isLoading={isLoading}
                    disabled={member ? !member.email : plan?.accounts_to_create === 0}
                >
                    {member ? t('clubMember.accounts.submitSingle') : t('clubMember.accounts.submit')}
                </FormInput.Submit>
            </Form>
        </Loader>
    );
};

export default ClubMemberAccountForm;
